import { reactive } from 'vue';
import { ApiRoutes, type ApiRouteKey } from '@codot/contracts';
import { i18n } from '../i18n';

/**
 * Confirmations, shown by <ToastHost> in the app shell with a draining progress bar (pauses on hover).
 * Besides explicit toast() calls, every successful create / update / delete request gets one automatically
 * (autoToast, from api/http.ts); an explicit toast for the same action replaces the automatic one.
 */
export interface ToastAction {
  label: string;
  run: () => void | Promise<void>;
}
export interface Toast {
  id: number;
  message: string;
  tone: 'success' | 'error' | 'info' | 'warning';
  action?: ToastAction;
  /** Several buttons (e.g. Publish · View in Content); shown after `action`. */
  actions?: ToastAction[];
  /** Total time on screen and what is left (the bar shows remaining / ms). */
  ms: number;
  remaining: number;
  paused: boolean;
  auto: boolean;
}
export const toasts = reactive<Toast[]>([]);
let seq = 0;
const timers = new Map<number, { handle: ReturnType<typeof setTimeout>; since: number }>();
let lastExplicitAt = 0;

function start(t: Toast): void {
  timers.set(t.id, { handle: setTimeout(() => dismiss(t.id), t.remaining), since: Date.now() });
}

function push(message: string, opts: { tone?: Toast['tone']; action?: Toast['action']; actions?: ToastAction[]; ms?: number }, auto: boolean): Toast {
  // The same message already showing: restart its timer instead of stacking a copy.
  const same = toasts.find((x) => x.message === message && x.tone === (opts.tone ?? 'success'));
  if (same) {
    const h = timers.get(same.id);
    if (h) clearTimeout(h.handle);
    same.remaining = same.ms;
    if (!same.paused) start(same);
    return same;
  }
  const ms = opts.ms ?? (opts.action || opts.actions?.length ? 7000 : opts.tone === 'error' ? 6000 : 4000);
  toasts.push({ id: ++seq, message, tone: opts.tone ?? 'success', action: opts.action, actions: opts.actions, ms, remaining: ms, paused: false, auto });
  const t = toasts.at(-1)!;
  start(t);
  if (toasts.length > 4) dismiss(toasts[0]!.id);
  return t;
}

export function toast(message: string, opts: { tone?: Toast['tone']; action?: Toast['action']; actions?: ToastAction[]; ms?: number } = {}): void {
  lastExplicitAt = Date.now();
  // The screen said it better: drop automatic toasts from the last moments (and any still pending).
  for (const t of [...toasts]) if (t.auto && Date.now() - (timers.get(t.id)?.since ?? 0) < 2500) dismiss(t.id);
  pending.forEach(clearTimeout);
  pending.clear();
  push(message, opts, false);
}

export function dismiss(id: number): void {
  const tm = timers.get(id);
  if (tm) clearTimeout(tm.handle);
  timers.delete(id);
  const i = toasts.findIndex((t) => t.id === id);
  if (i >= 0) toasts.splice(i, 1);
}

/** Hovering or focusing a toast stops its clock; leaving resumes it. */
export function pause(id: number): void {
  const t = toasts.find((x) => x.id === id);
  const tm = timers.get(id);
  if (!t || !tm || t.paused) return;
  clearTimeout(tm.handle);
  t.remaining = Math.max(0, t.remaining - (Date.now() - tm.since));
  t.paused = true;
}
export function resume(id: number): void {
  const t = toasts.find((x) => x.id === id);
  if (!t || !t.paused) return;
  t.paused = false;
  start(t);
}

/* ---------------- automatic create / update / delete toasts ---------------- */

type Verb = 'created' | 'saved' | 'deleted';
/** Route → [what, verb] or a ready message key under `toast.`. Routes not listed get no automatic toast. */
const AUTO: Partial<Record<ApiRouteKey, Partial<Record<string, [string, Verb] | string>>>> = {
  layouts: { POST: ['campaign', 'created'] },
  layout: { PUT: ['campaign', 'saved'], DELETE: ['campaign', 'deleted'] },
  playlists: { POST: ['playlist', 'created'] },
  playlist: { PUT: ['playlist', 'saved'], DELETE: ['playlist', 'deleted'] },
  mediaAssets: { POST: ['app', 'created'] },
  mediaAsset: { PATCH: ['content', 'saved'], DELETE: ['content', 'deleted'] },
  folders: { POST: ['folder', 'created'] },
  folder: { PATCH: ['folder', 'saved'], DELETE: ['folder', 'deleted'] },
  contentMove: { POST: 'moved' },
  contentTags: { POST: 'tagsSaved' },
  contentAttributes: { PUT: 'attributesSaved' },
  contentRestriction: { PUT: 'restrictionSaved' },
  groups: { POST: ['group', 'created'] },
  group: { PATCH: ['group', 'saved'], DELETE: ['group', 'deleted'] },
  groupTags: { PUT: 'tagsSaved' },
  groupContentOrder: { PUT: 'orderSaved' },
  screens: { POST: ['player', 'created'] },
  screen: { PATCH: ['player', 'saved'], DELETE: ['player', 'deleted'] },
  screenDevice: { POST: 'paired', DELETE: 'unpaired' },
  devicePairingClaim: { POST: 'paired' },
  screenSettings: { PUT: 'settingsSaved' },
  screenTags: { PUT: 'tagsSaved' },
  playerAttributes: { PUT: 'attributesSaved' },
  playerContent: { PATCH: 'settingsSaved' },
  playerContentOrder: { PUT: 'orderSaved' },
  playerDefaults: { PUT: 'settingsSaved' },
  publish: { POST: 'published' },
  screenPublish: { POST: 'published' },
  placementsBulk: { POST: 'settingsSaved' },
  placement: { PATCH: 'settingsSaved', DELETE: 'unpublished' },
  placementExclusion: { DELETE: 'settingsSaved' },
  tagPublication: { DELETE: 'unpublished' },
  layoutInteractions: { POST: ['interaction', 'created'] },
  screenInteractions: { POST: ['interaction', 'created'] },
  interaction: { PUT: ['interaction', 'saved'], DELETE: ['interaction', 'deleted'] },
  layoutTemplates: { POST: ['layout', 'created'] },
  layoutTemplate: { PUT: ['layout', 'saved'], DELETE: ['layout', 'deleted'] },
  alerts: { POST: ['alert', 'created'] },
  alert: { PUT: ['alert', 'saved'], DELETE: ['alert', 'deleted'] },
  attributes: { POST: ['attribute', 'created'] },
  attribute: { PUT: ['attribute', 'saved'], DELETE: ['attribute', 'deleted'] },
  customApp: { PATCH: ['customApp', 'saved'], DELETE: ['customApp', 'deleted'] },
  customAppVersionCurrent: { POST: 'versionCurrent' },
  dataFeeds: { POST: ['dataFeed', 'created'] },
  dataFeed: { PUT: ['dataFeed', 'saved'], DELETE: ['dataFeed', 'deleted'] },
  dataFeedApprove: { POST: 'approved' },
  dataFeedReject: { POST: 'rejected' },
  dataFeedSync: { POST: 'syncStarted' },
  regions: { POST: ['region', 'created'] },
  region: { PUT: ['region', 'saved'], DELETE: ['region', 'deleted'] },
  reports: { POST: ['report', 'created'] },
  report: { PUT: ['report', 'saved'], DELETE: ['report', 'deleted'] },
  reportRun: { POST: 'reportStarted' },
  healthAcknowledge: { POST: 'acknowledged' },
  healthSettings: { PUT: 'settingsSaved' },
  notificationPrefs: { PUT: 'settingsSaved' },
  organizationProfile: { PATCH: 'settingsSaved' },
  organizationInvitations: { POST: 'inviteSent' },
  organizationInvitation: { DELETE: 'inviteRevoked' },
  organizationMember: { PATCH: 'roleChanged', DELETE: 'memberRemoved' },
  meProfile: { PATCH: 'settingsSaved' },
  meAvatar: { DELETE: 'settingsSaved' },
  mePassword: { POST: 'passwordChanged' },
  meSession: { DELETE: 'signedOutDevice' },
  meSessionsRevokeOthers: { POST: 'signedOutOthers' },
};
const MATCHERS = (Object.keys(AUTO) as ApiRouteKey[]).map((key) => ({
  key,
  re: new RegExp(`^/api/v1/${ApiRoutes[key].replace(/:[A-Za-z]+/g, '[^/]+')}/?(\\?.*)?$`),
}));
const pending = new Map<string, ReturnType<typeof setTimeout>>();

/** Called by api/http.ts after a successful POST / PUT / PATCH / DELETE. */
export function autoToast(method: string, path: string): void {
  const hit = MATCHERS.find((m) => m.re.test(path));
  const spec = hit && AUTO[hit.key]?.[method];
  if (!spec) return;
  const { t } = i18n.global;
  const message = typeof spec === 'string' ? t(`toast.${spec}`) : t(`toast.${spec[1]}`, { thing: t(`toast.n_${spec[0]}`) });
  if (Date.now() - lastExplicitAt < 1500) return; // the screen already confirmed it
  // Bulk actions (10 deletes) say it once; a later explicit toast cancels this before it shows.
  if (pending.has(message) || toasts.some((x) => x.auto && x.message === message)) return;
  pending.set(
    message,
    setTimeout(() => {
      pending.delete(message);
      if (Date.now() - lastExplicitAt < 1500) return;
      push(message, {}, true);
    }, 600),
  );
}
